import {
  useGetOneFlow,
  useListFlowProductCategories,
  useListFlowProductTypes,
  useListFlowTypes,
  useUpdateFlow,
} from "#src/components/hooks/adapters/useFlows";
import useLocalization from "#src/hooks/useLocalization";
import { AssetType, Resources } from "@validereinc/domain";
import { Flows } from "@validereinc/domain-controllers";
import React from "react";
import AssetEditDialog from "./AssetEditDialog/AssetEditDialog";
import { AssetEditDialogPropTypes } from "./AssetEditDialog/types";
import { useQueryClient } from "@tanstack/react-query";

const { FlowStatusOptions } = Flows.view;

const EditFlowDialog = (props: {
  flowId?: string;
  defaultPeriod: string;
  onClose: () => void;
  onEdit?: () => void;
}) => {
  const { localize } = useLocalization();
  const queryClient = useQueryClient();

  const { data: assetData, isLoading: isLoadingDefaultValues } = useGetOneFlow(
    {
      id: props.flowId ?? "",
      meta: {
        period: props.defaultPeriod,
      },
    },
    { enabled: !!props.flowId }
  );
  const assetDetails = assetData?.data;
  const updateFlowMutation = useUpdateFlow({
    onSuccess: () => {
      props.onEdit?.();
      props.onClose();
      queryClient.invalidateQueries({
        queryKey: [Resources.ACTIVITY],
      });
    },
  });

  const { data: flowTypes, isLoading: isLoadingFlowTypes } = useListFlowTypes();
  const { data: productCategories, isLoading: isLoadingProductCategories } =
    useListFlowProductCategories();
  const { data: productTypes, isLoading: isLoadingProductTypes } =
    useListFlowProductTypes();

  const isUpdating = updateFlowMutation.isLoading;
  const isLoading =
    isUpdating ||
    isLoadingDefaultValues ||
    isLoadingFlowTypes ||
    isLoadingProductCategories ||
    isLoadingProductTypes;

  const primaryFields: AssetEditDialogPropTypes["primaryFields"] = [
    {
      display_name: `${localize("flow")} name`,
      field_name: "name",
      data_type: "string",
      is_required: true,
      is_state_managed: false,
    },
    {
      display_name: "Type",
      field_name: "type",
      data_type: "pick-list",
      pick_list_values: flowTypes?.data ?? [],
      is_required: true,
      is_state_managed: false,
    },
    {
      display_name: "Status",
      field_name: "status",
      data_type: "pick-list",
      pick_list_values: Object.values(FlowStatusOptions).map(
        ({ label, value }) => ({
          id: value,
          name: label,
        })
      ),
      is_required: true,
      is_state_managed: true,
    },
    {
      display_name: "Product Category",
      field_name: "product_category",
      data_type: "pick-list",
      pick_list_values: productCategories?.data ?? [],
      is_required: true,
      is_state_managed: false,
    },
    {
      display_name: "Product Type",
      field_name: "product_type",
      data_type: "pick-list",
      pick_list_values: productTypes?.data ?? [],
      is_required: true,
      is_state_managed: false,
    },
    {
      display_name: `Associated ${localize("Facility")}`,
      field_name: "associated_facility_id",
      data_type: "lookup",
      lookup_entity_type: AssetType.FACILITY,
      is_state_managed: false,
    },
    {
      display_name: `Associated ${localize("Equipment")}`,
      field_name: "associated_equipment_id",
      data_type: "lookup",
      lookup_entity_type: AssetType.EQUIPMENT,
      is_state_managed: false,
    },
  ];

  return (
    <AssetEditDialog
      primaryFields={primaryFields}
      assetId={props.flowId}
      assetType={AssetType.FLOW}
      dialogProps={{ title: `Edit ${localize("flow")} Attributes` }}
      onClose={() => {
        if (isUpdating) return;
        props.onClose();
      }}
      isLoading={isLoading}
      onUpdate={(newValues) =>
        updateFlowMutation.mutate({
          id: props.flowId ?? "",
          data: {
            ...newValues,
          },
        })
      }
      assetDetails={assetDetails}
    />
  );
};

export default EditFlowDialog;
